// SPDX-FileCopyrightText: 2017-2022 City of Espoo
//
// SPDX-License-Identifier: LGPL-2.1-or-later

import React from 'react'

import {
  daycareAssistanceLevels,
  preschoolAssistanceLevels
} from 'lib-common/generated/api-types/assistance'
import type { EmployeeCustomizations } from 'lib-customizations/types'

import { employeeConfig } from './appConfigs'
import MunicipalityLogo from './assets/municipality-logo-primary.svg'
import featureFlags from './featureFlags'

const customizations: EmployeeCustomizations = {
  appConfig: employeeConfig,
  translations: {
    fi: {
      common: {
        retroactiveConfirmation: {
          checkboxLabel:
            'Ymmärrän, olen asiasta yhteydessä laskutustiimiin *'
        }
      },
      application: {
        additionalInfo: {
        title: 'Lisätiedot, kirjaa alle lapsen päivittäinen hoitoaika ja toive metsäeskariin pääsystä',
        }
      },
      footer: {
        cityLabel: 'Kouvolan kaupunki',
        linkLabel: 'Kouvolan varhaiskasvatus',
        linkHref: 'https://www.kouvola.fi/kasvu-ja-oppiminen/paivahoito-ja-varhaiskasvatus/'
      },
      unit: {
        staffAttendance: {
          types: {
            SICKNESS: 'Sairaus',
            CHILD_SICKNESS: 'Lapsen sairaus'
          },
        },
      },
       placement: {
        type: {
            SCHOOL_SHIFT_CARE: 'Koululaisten kokopäivähoito'
        },
      },
      absences: {
        absenceTypes: {
          PLANNED_ABSENCE: 'Ennalta ilmoitettu poissaolo',
        },
        absenceTypeInfo: {
          PLANNED_ABSENCE:
            'Poissaolo, joka ei kuluta palveluntarvetta. Merkitään, jos huoltaja on ilmoittanut lapsen poissaolon etukäteen ajoissa.',
        },
        absenceTypesShort: {
          PLANNED_ABSENCE: 'Ilmoitettu',
        },
        careTypes: {
          SCHOOL_SHIFT_CARE: 'Koululaisten kokopäivähoito',
        },
        modal: {
          absenceSectionLabel: 'Poissaolon syy',
          placementSectionLabel: 'Toimintamuoto, jota poissaolo koskee',
          saveButton: 'Tallenna',
          cancelButton: 'Peruuta',
          absenceTypes: {
            PLANNED_ABSENCE: 'Ennalta ilmoitettu poissaolo',
          },
        },
      },
      unitEditor: {
        label: {
          dwCostCenter: 'Palvelusuhde (pph)',
        },
        placeholder: {
          email: 'etunimi.sukunimi@kouvola.fi',
          url: 'esim. https://www.kouvola.fi/fi/toimipisteet/15585',
          dwCostCenter: 'Palvelusuhdeluokan kirjain syötetään tähän',
        }
      },
      welcomePage: {
        text: 'Olet kirjautunut sisään Kouvolan kaupungin eVaka-palveluun. Käyttäjätunnuksellesi ei ole vielä annettu oikeuksia, jotka mahdollistavat palvelun käytön. Tarvittavat käyttöoikeudet saat omalta esimieheltäsi.'
      },
      preferredFirstName: {
        description:
          'Voit määritellä eVakassa käytössä olevan kutsumanimesi. Kutsumanimen tulee olla jokin etunimistäsi. Jos nimesi on vaihtunut ja sinulla on tarve päivittää eVakaan uusi nimesi, ole yhteydessä Kouvolan tukeen.',
      },
      login: {
        loginAD: 'Kouvolan AD',
      },
      incomeStatement: {
        noIncomeTitle:
        'Ei mitään tuloja tai tukia, tiedot saa tarkastaa Kelasta sekä tulorekisteristä',
        incomesRegister:
          'Hyväksyn, että tuloihini liittyviä tietoja tarkastellaan Kelasta sekä tulorekisteristä.',
        checkupConsent:
        'Hyväksyn, että tuloihini liittyviä tietoja tarkastellaan tarvittaessa Kelasta sekä tulorekisteristä.',
        limitedCompanyIncomesRegister:
        'Tuloni voi tarkastaa tarvittaessa Kelasta sekä tulorekisteristä.',
      },
    },
    sv: {},
  },

  cityLogo: <img src={MunicipalityLogo} alt="Kouvola logo" data-qa="footer-city-logo" />,
  featureFlags,
  placementTypes: [
    'PRESCHOOL',
    'PRESCHOOL_DAYCARE',
    'DAYCARE',
    'DAYCARE_PART_TIME',
    'PREPARATORY',
    'PREPARATORY_DAYCARE',
    'CLUB',
    'TEMPORARY_DAYCARE',
    'TEMPORARY_DAYCARE_PART_DAY',
    'SCHOOL_SHIFT_CARE',
  ],
  absenceTypes: [
    'OTHER_ABSENCE',
    'SICKLEAVE',
    'UNKNOWN_ABSENCE',
    'PLANNED_ABSENCE',
    'PARENTLEAVE',
    'FORCE_MAJEURE',
  ],
  daycareAssistanceLevels: [...daycareAssistanceLevels],
  otherAssistanceMeasureTypes: [
    'TRANSPORT_BENEFIT',
    'ACCULTURATION_SUPPORT',
    'ANOMALOUS_EDUCATION_START',
  ],
  placementPlanRejectReasons: ['REASON_1', 'REASON_2', 'OTHER'],
  preschoolAssistanceLevels: [...preschoolAssistanceLevels],
  unitProviderTypes: [
    'MUNICIPAL',
    'PURCHASED',
    'PRIVATE',
    'MUNICIPAL_SCHOOL',
    'PRIVATE_SERVICE_VOUCHER',
    'EXTERNAL_PURCHASED',
  ],
  voucherValueDecisionTypes: [
    'NORMAL',
    'RELIEF_ACCEPTED',
    'RELIEF_PARTLY_ACCEPTED',
    'RELIEF_REJECTED',
  ],
  additionalStaffAttendanceTypes: [
    'OTHER_WORK',
    'TRAINING',
    'JUSTIFIED_CHANGE',
    'SICKNESS',
    'CHILD_SICKNESS',
  ],

}

export default customizations
